import { router } from '@inertiajs/react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import * as React from 'react';
import Copyright from '../../internals/components/Copyright';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const getRoleIcon = (roles) => {
  if (roles.includes('god_mode')) {
    return <AdminPanelSettingsIcon sx={{ fontSize: 16 }} />;
  }

  if (roles.includes('full')) {
    return <PersonIcon sx={{ fontSize: 16 }} />;
  }

  return <PersonIcon sx={{ fontSize: 16 }} />;
};

const getRoleLabel = (roles) => {
  if (roles.includes('god_mode')) {
    return 'Administrador';
  }

  if (roles.includes('full')) {
    return 'Usuário';
  }

  return 'Sem Perfil';
};

const getRoleColor = (roles) => {
  if (roles.includes('god_mode')) {
    return 'error';
  }

  if (roles.includes('full')) {
    return 'primary';
  }

  return 'default';
};

const handleEdit = (edit_url) => {
  router.visit(edit_url);
};

export default function UsersTable({ users = [] }) {
  const [deleteDialog, setDeleteDialog] = React.useState({
    open: false,
    userName: '',
    destroyUrl: '',
    loading: false
  });

  // Ensure we have an array and add unique IDs
  const rows = Array.isArray(users) ? users.map((user, index) => ({
    id: user.id || index,
    ...user,
  })) : [];

  const handleDeleteClick = (destroy_url, userName) => {
    setDeleteDialog({
      open: true,
      userName,
      destroyUrl: destroy_url,
      loading: false
    });
  };

  const handleDeleteConfirm = () => {
    setDeleteDialog(prev => ({ ...prev, loading: true }));

    router.delete(deleteDialog.destroyUrl, {
      preserveScroll: true,
      onSuccess: () => {
        setDeleteDialog({
          open: false,
          userName: '',
          destroyUrl: '',
          loading: false
        });
      },
      onError: (errors) => {
        setDeleteDialog(prev => ({ ...prev, loading: false }));
        // You could show an error message here
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      userName: '',
      destroyUrl: '',
      loading: false
    });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nome',
      width: 200,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      flex: 1.5,
    },
    {
      field: 'phone',
      headerName: 'Telefone',
      width: 80,
      flex: 1,
    },
    {
      field: 'roles',
      headerName: 'Perfil',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Chip
            label={getRoleLabel(params.value)}
            size="small"
            color={getRoleColor(params.value)}
            icon={getRoleIcon(params.value)}
          />
        </Box>
      ),
    },
    {
      field: 'carrier_name',
      headerName: 'Transportadora',
      width: 180,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
          {params.value ? <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} /> : null}
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'last_sign_in_at',
      headerName: 'Último Acesso',
      width: 130,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {formatDate(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Criado em',
      width: 110,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {formatDate(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 0.5
        }}>
          <Tooltip title="Editar usuário">
            <IconButton
              sx={{
                height: '30px',
                width: '35px',
              }}
              size="small"
              onClick={() => handleEdit(params.row.edit_url)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir usuário">
            <IconButton
              sx={{
                height: '30px',
                width: '35px',
              }}
              size="small"
              color="warning"
              onClick={() => handleDeleteClick(params.row.destroy_url, params.row.name)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Usuários
      </Typography>

      <DataGrid
        checkboxSelection
        disableRowSelectionOnClick
        rows={rows}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableColumnResize
        density="compact"
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir o usuário{' '}
            <strong>{deleteDialog.userName}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteDialog.loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="warning"
            variant="contained"
            disabled={deleteDialog.loading}
          >
            {deleteDialog.loading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
} 